import React, { useEffect, useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import './VideoSlider.css'

/* How long each slide holds. Shared with the CSS so the progress bars run in
   step with the autoplay rather than guessing at it. */
const SLIDE_INTERVAL = 12000;
const TRANSITION = 900;

const VideoSlider = ({ videos }) => {
  /* Only the active slide and the one after it carry a video element. The
     carousel keeps every slide mounted, and with autoPlay on all of them the
     browser started four full-length downloads at once, so the first slide
     had to share bandwidth with the other three before it could play. The
     outgoing slide is retained for the length of the cross-fade so it does
     not drop to black mid-transition. */
  const [active, setActive] = useState(0);
  const [outgoing, setOutgoing] = useState(null);
  const activeRef = useRef(0);

  const handleChange = (index) => {
    setOutgoing(activeRef.current);
    activeRef.current = index;
    setActive(index);
  };

  useEffect(() => {
    if (outgoing === null) return undefined;
    const t = setTimeout(() => setOutgoing(null), TRANSITION + 300);
    return () => clearTimeout(t);
  }, [outgoing]);

  const count = videos.length;
  const isMounted = (index) =>
    index === active || index === (active + 1) % count || index === outgoing;

  /* Timed bars instead of dots: the hero advances on its own, so the control
     should say how long is left rather than only where you are. The fill is
     driven by a CSS animation that restarts whenever `selected` is applied. */
  const renderIndicator = (onClick, isSelected, index, label) => (
    <li
      className={`hero-tick ${isSelected ? 'is-selected' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      }}
      value={index}
      key={index}
      role="button"
      tabIndex={0}
      aria-label={`${label} ${index + 1}`}
      aria-current={isSelected}
    >
      <span className="hero-tick-fill" />
    </li>
  );

  return (
    <Carousel
      infiniteLoop
      autoPlay
      interval={SLIDE_INTERVAL}
      /* Cross-fade rather than a horizontal push: the slides are full-bleed
         footage, and sliding them sideways reads as a widget. */
      animationHandler="fade"
      transitionTime={TRANSITION}
      swipeable={false}
      stopOnHover={false}
      renderIndicator={renderIndicator}
      onChange={handleChange}
      style={{ '--hero-interval': `${SLIDE_INTERVAL}ms` }}
    >
      {videos.map((video, index) => (
        <div key={index} className={index === 0 ? 'first-slide' : ''}>
          <div className="video-wrapper">
            {isMounted(index) && (
              /* src on the element rather than a <source type="video/mp4">:
                 two of these files are .mov, and a wrong type hint can make
                 a browser skip the source outright. */
              <video
                src={video.src}
                autoPlay
                muted
                loop
                playsInline
                preload={index === active ? 'auto' : 'metadata'}
              />
            )}
          </div>
          <div className="slide-caption">
            {video.subtitle && <p className="video-subtitle">{video.subtitle}</p>}
            {video.title && <p className="video-title">{video.title}</p>}
            {video.thirdtext && <p className="text-three">{video.thirdtext}</p>}
            {video.logo && <img className="ut-logo" src={video.logo} alt="University Logo" />}
          </div>
        </div>
      ))}
    </Carousel>
  );
};

export default VideoSlider;
